import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getOffsetDate } from 'shared/lib/getOffsetDate';
import { useGetJsonParams } from 'shared/lib/useGetJsonParams';
import LineChart from 'shared/ui/LineChart/LineChart';
import { PeriodSelectModal } from 'shared/ui/PeriodSelectModal/PeriodSelectModal';
import { CareDownIcon, CareUpIcon, PeriodArrowIcon } from 'shared/ui/icon/ArrowIcon';

import { useGetSpecimenDetail } from '../api';
import { getLowHigh } from '../lib/getLowHigh';

export const SpecimenDetail = () => {
  const { specimenCode } = useParams();
  const { patientNumber, hspId, specimenEnName, specimenKrName, testDate } = useGetJsonParams();
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const [period, setPeriod] = useState(
    (() => {
      const date = new Date(testDate);
      const today = new Date();

      const isWithin = (month) => date >= new Date(today.getFullYear(), today.getMonth() - month, today.getDate());

      if (isWithin(6))
        return {
          key: '6/M',
          label: '최근 6개월',
        };
      if (isWithin(12))
        return {
          key: '1/Y',
          label: '최근 1년',
        };
      if (isWithin(24))
        return {
          key: '2/Y',
          label: '최근 2년',
        };
      else
        return {
          key: '3/Y',
          label: '최근 3년',
        };
    })(),
  );

  const { data } = useGetSpecimenDetail({
    patientNumber,
    specimenCode,
    hspId,
    searchStartDate: (() => {
      const [num, unit] = period.key.split('/');
      return getOffsetDate(-Number(num), unit).toISOString().slice(0, 10);
    })(),
    searchEndDate: new Date().toISOString().slice(0, 10),
  });

  const [initialData, setInitialData] = useState();

  useEffect(() => {
    if (!data || initialData) return;
    setInitialData(data.tests);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return [];
    else
      return data.tests.map((test) => ({
        value: test.testResult,
        unit: data.testResultUnit,
        xAxisLabel: (() => {
          const [year, month, date] = test.testDate.split('-');
          return `${year}<br />${('0' + month).slice(-2)}/${('0' + date).slice(-2)}`;
        })(),
      }));
  }, [data]);
  if (!data || !initialData) return null;

  return (
    <div className='lab-result'>
      {/* 헤더 */}
      <header className='lab-result__header'>
        <h1 className='lab-result__title'>{specimenKrName}</h1>
        <p className='lab-result__subtitle'>{specimenEnName}</p>
      </header>

      {/* 최근검사결과카드 */}
      {(() => {
        const { isHigh, isLow } = getLowHigh(
          data.referenceMinValue,
          data.referenceMaxValue,
          initialData[initialData.length - 1].testResult,
        );

        return (
          <section className='lab-result__summary'>
            <div className='lab-result__value-card'>
              <div className='lab-result-title'>
                <p className='lab-result__label'>최근 검사 결과</p>
                <div className='lab-result__value'>
                  <strong className='lab-result__number'>{initialData[initialData.length - 1].testResult}</strong>
                  <span className='lab-result__unit'>{data.testResultUnit}</span>
                  {isHigh ? (
                    <span className='lab-result__status lab-result__status--high'>
                      <CareUpIcon />
                      <span>높아요</span>
                    </span>
                  ) : isLow ? (
                    <span className='lab-result__status lab-result__status--low'>
                      <CareDownIcon />
                      <span>낮아요</span>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className='lab-result__meta'>
                <span className='lab-result__meta__item lab-result__date'>
                  <span>검사일</span>
                  <span>{testDate.replace(/-/g, '.')}</span>
                </span>
                {(data.referenceMinValue || data.referenceMaxValue) && (
                  <span className='lab-result__meta__item lab-result__range'>
                    <span>참고치</span>
                    <span>
                      {data.referenceMinValue && data.referenceMaxValue
                        ? `${data.referenceMinValue} ~ ${data.referenceMaxValue}`
                        : (() => {
                            const value = data.referenceMinValue || data.referenceMaxValue;
                            return value;
                          })()}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      {/* 검사이력 차트 */}
      <section className='lab-result__chart'>
        <div className='lab-result__history-header'>
          <h2 className='lab-result__section-title'>검사 이력</h2>
          <button
            className={`lab-result__filter ${isPeriodOpen ? 'is-open' : ''}`}
            onClick={() => setIsPeriodOpen(true)}
          >
            <span>{period.label}</span>
            <PeriodArrowIcon />
          </button>
        </div>
        <div style={{ width: '100%', height: '220px', margin: '20px auto 0' }}>
          <LineChart
            data={chartData}
            referenceRange={
              data.referenceMinValue || data.referenceMaxValue
                ? { top: Number(data.referenceMaxValue), bottom: Number(data.referenceMinValue) }
                : null
            }
          />
        </div>
      </section>

      {/* 검사이력 테이블 */}
      <section className='lab-result__table'>
        {!data.tests?.length ? (
          <div className='lab-result__no-data'>
            {period.label}
            <br />
            검사 내역이 없어요!
          </div>
        ) : (
          <>
            <p className='lab-result__chart-unit'>단위: {data.testResultUnit}</p>

            <table className='lab-result__table-list'>
              <colgroup>
                <col style={{ width: '' }} />
                <col style={{ width: '' }} />
                <col style={{ width: '' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>검사일</th>
                  <th>결과</th>
                  <th>{data.referenceMaxValue || data.referenceMinValue ? '비교' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {[...data.tests].reverse().map((test, idx) => (
                  <tr key={test.testDate + idx}>
                    <td>{test.testDate.replace(/-/g, '.')}</td>
                    <td>{test.testResult}</td>
                    <td>
                      {(() => {
                        if (!data.referenceMaxValue && !data.referenceMinValue) return null;
                        const { isHigh, isLow } = getLowHigh(
                          data.referenceMinValue,
                          data.referenceMaxValue,
                          test?.testResult,
                        );

                        if (isHigh) return <span className='high'>높음</span>;
                        else if (isLow) return <span className='low'>낮음</span>;
                        else return <span className='normal'>정상</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* 안내문구 추가 */}
      {(period.key === '3/Y' || !data.tests?.length) && (
        <div className='lab-result__notice'>
          <p>병원 사정으로 검사 기록이 조회되지 않을 수도 있어요.</p>
        </div>
      )}

      <PeriodSelectModal
        period={period}
        onSelect={(item) => {
          setPeriod(item);
        }}
        isPeriodOpen={isPeriodOpen}
        setIsPeriodOpen={setIsPeriodOpen}
        options={[
          {
            key: '6/M',
            label: '최근 6개월',
          },
          {
            key: '1/Y',
            label: '최근 1년',
          },
          {
            key: '2/Y',
            label: '최근 2년',
          },
          {
            key: '3/Y',
            label: '최근 3년',
          },
        ]}
      />
    </div>
  );
};

